import {
  DefaultMaxColumns,
  DefaultMaxRows,
  getEntityIdFromPosition,
  PositionInstructions,
} from "@coolset/cs-ui";
import { Typography } from "@mui/material";
import { blockToBlockable } from "modules/MainSurveyEditor/helpers/blockTree";
import { useBlockFormValues } from "modules/MainSurveyEditor/hooks/useBlockFormValues";
import { CopyAndPasteBlocksStore } from "modules/MainSurveyEditor/hooks/useCopyAndPasteBlocks";
import { usePositioningTable } from "modules/MainSurveyEditor/hooks/usePositioningTable";
import React, { useCallback, useEffect, useState } from "react";
import { BlockIcon } from "shared/components/BlockOption/BlockIcon";
import { DynamicBlocksTable } from "shared/components/Table/DynamicBlocksTable";
import { getBlocksInRows } from "shared/components/Table/tableSelection";
import { mainSurveyClient } from "../../client/mainSurveyClient";
import { BlockDefaults, BlockFieldTypes } from "../../helpers/blockForm";
import { BlockSubTypes } from "../../helpers/blocks";
import { MainSurveyStore } from "../../hooks/useMainSurvey";
import { useMainSurveyData } from "../../hooks/useMainSurveyData";
import { SurveyBlocks } from "../../surveyBlocks";
import { ManageDataSet } from "../SubSections/ManageDataSet";
import { PasteBlocksDialog } from "../SubSections/PasteBlocksDialog";
import { CopyPasteBlockActions } from "./CopyPasteBlockActions";

export const TableEdit = (props: TableEditProps) => {
  const {
    mainSurveyStore: {
      tableBlocks,
      setTableBlocks,
      isTableEditOpen,
      blockToEdit,
      subBlockToEdit,
      setSubBlockToEdit,
      newBlockTypeToBeAdded,
      setNewBlockTypeToBeAdded,
      setBlockToAdd,
      existingBlockToAdd,
      draggedBlockPosition,
      setDraggedBlockPosition,
      handleAddExistingBlock,
      handleChangedOrAddedSubBlock,
      resetSubBlockToEdit,
      selectedTableBlocks,
      setSelectedTableBlocks,
      refetchCurrentBlock,
    },
    copyAndPasteBlocksStore,
  } = props;

  const { surveyBlock, isLoading } = useMainSurveyData(
    blockToEdit?.parentSurveyId,
    blockToEdit?.id,
  );
  const [selectedBlock, setSelectedBlock] = useState<SurveyBlocks.Block | null>(
    null,
  );
  const [selectionArea, setSelectionArea] = useState<string>("");
  const [isLoadingBlocks, setIsLoadingBlocks] = useState<boolean>(false);

  const blockFormValuesStore = useBlockFormValues(
    blockToEdit?.id,
    blockToEdit?.parentSurveyId,
  );
  const positionInstructionsStore = usePositioningTable(
    blockFormValuesStore,
    tableBlocks || [],
  );
  const { isSaving: isSavingBlockFormValues, setBlockValues, setLastBlockValues } = blockFormValuesStore;
  const {
    positionInstructions,
    handleRepositionBlocks,
    addBlockPosition,
    setIsAwaitingBlockUpdate,
  } = positionInstructionsStore;

  const {
    setCopiedBlocks,
    startPaste,
    copiedBlocks,
    setCopiedFormattingInstructions,
    setFormattingInstructionsToPaste,
    setCopiedPositionInstructions,
    setPositionInstructionsToPaste,
  } = copyAndPasteBlocksStore;

  const getBlocks = useCallback(async () => {
    setIsLoadingBlocks(true);
    const {
      data: { results: nestedBlocks },
    } = await mainSurveyClient.getSurveyBlocks(surveyBlock!.objectId);

    setTableBlocks(nestedBlocks);
    setIsLoadingBlocks(false);
  }, [surveyBlock, setTableBlocks]);

  useEffect(() => {
    if (!subBlockToEdit && tableBlocks?.length && positionInstructions) {
      resetSubBlockToEdit(tableBlocks, positionInstructions);
    }
  }, [subBlockToEdit, tableBlocks, positionInstructions, resetSubBlockToEdit]);

  const handleDrop = useCallback(
    async (position: string) => {
      if (!selectedBlock) return;

      setIsAwaitingBlockUpdate(true);

      if (newBlockTypeToBeAdded) {
        setBlockToAdd({
          parentSurveyId: selectedBlock.objectId,
          type: newBlockTypeToBeAdded,
          position: position,
          callBack: (block) => addBlockPosition(block, position),
        });
        setNewBlockTypeToBeAdded(null);

        return;
      }

      if (existingBlockToAdd) {
        await handleAddExistingBlock({
          parentId: selectedBlock.objectId,
          position,
          callBack: (block) => addBlockPosition(block, position),
        });

        return;
      }

      // repositioning the block
      if (!draggedBlockPosition) return;

      const blockId = getEntityIdFromPosition(
        positionInstructions,
        draggedBlockPosition,
      );
      const block = (tableBlocks || []).find((b) => String(b.id) === blockId);
      if (!block) return;

      await handleRepositionBlocks({
        [String(blockId)]: {
          oldPosition: draggedBlockPosition,
          newPosition: position,
        },
      });
      handleChangedOrAddedSubBlock(block);
    },
    [
      draggedBlockPosition,
      selectedBlock,
      newBlockTypeToBeAdded,
      existingBlockToAdd,
      tableBlocks,
      positionInstructions,
      setBlockToAdd,
      setNewBlockTypeToBeAdded,
      handleAddExistingBlock,
      handleRepositionBlocks,
      handleChangedOrAddedSubBlock,
      addBlockPosition,
      setIsAwaitingBlockUpdate,
    ],
  );

  const handleBlockClick = useCallback(
    (e: React.MouseEvent, block: SurveyBlocks.Block) => {
      if (!selectedBlock) return;

      if (e.metaKey) {
        setSelectedTableBlocks((prev) => {
          if (prev.find((b) => b.id === block.id)) {
            return prev.filter((b) => b.id !== block.id);
          }

          return [...prev, block];
        });

        return;
      }

      setSubBlockToEdit({
        id: block.id,
        parentSurveyId: selectedBlock.objectId,
        type: block.contentType,
      });

      setSelectedTableBlocks([block]);
    },
    [selectedBlock, setSelectedTableBlocks, setSubBlockToEdit],
  );

  const handleRowSelectionChange = useCallback(
    (rowPositions: number[]) => {
      const blocksInRows = getBlocksInRows(rowPositions, tableBlocks || [], positionInstructions);
      setSelectedTableBlocks(blocksInRows);

      if (blocksInRows.length === 1 && selectedBlock) {
        const block = blocksInRows[0];
        setSubBlockToEdit({ id: block.id, parentSurveyId: selectedBlock.objectId, type: block.contentType });
      }
    },
    [tableBlocks, positionInstructions, setSelectedTableBlocks, setSubBlockToEdit, selectedBlock],
  );

  const handleOnCopy = useCallback(() => {
    setCopiedBlocks(selectedTableBlocks.map((b) => blockToBlockable(b)));
    const copiedPositionInstructions: PositionInstructions = {};
    selectedTableBlocks.forEach(
      (b) =>
        (copiedPositionInstructions[String(b.id)] =
          positionInstructions[String(b.id)]),
    );
    setCopiedPositionInstructions(copiedPositionInstructions);
    setCopiedFormattingInstructions(
      selectedBlock?.contentObject.formattingInstructions ?? [],
    );
  }, [
    selectedBlock,
    selectedTableBlocks,
    setCopiedBlocks,
    setCopiedFormattingInstructions,
    positionInstructions,
    setCopiedPositionInstructions,
  ]);

  const handleClearCopiedBlocks = useCallback(() => {
    setCopiedBlocks([]);
    setCopiedPositionInstructions(null);
    setCopiedFormattingInstructions([]);
  }, [
    setCopiedBlocks,
    setCopiedFormattingInstructions,
    setCopiedPositionInstructions,
  ]);

  const handleBulkDeleted = useCallback(
    (deletedIds: string[]) => {
      const remaining = (tableBlocks || []).filter((b) => !deletedIds.includes(String(b.id)));
      setTableBlocks(remaining);
      setSelectedTableBlocks([]);
      resetSubBlockToEdit(remaining, positionInstructions);
    },
    [tableBlocks, setTableBlocks, setSelectedTableBlocks, resetSubBlockToEdit, positionInstructions],
  );

  // After a bulk conditions/supporting-info fan-out, re-fetch the cells so the locally
  // held block objects (and any later re-seed of the bulk panel) reflect server truth.
  // Without this, changing the selection and re-applying could re-add a duplicate.
  const refreshAfterBulkEdit = useCallback(async () => {
    if (!selectedBlock) return;
    try {
      const {
        data: { results },
      } = await mainSurveyClient.getSurveyBlocks(selectedBlock.objectId);
      setTableBlocks(results);
      setSelectedTableBlocks((prev) => {
        const byId = new Map(results.map((b) => [String(b.id), b]));
        return prev.map((b) => byId.get(String(b.id)) ?? b);
      });
    } catch (error) {
      console.error(error);
    }
  }, [selectedBlock, setTableBlocks, setSelectedTableBlocks]);

  const handleOnPaste = useCallback(() => {
    if (!selectedBlock) return;

    setFormattingInstructionsToPaste(
      selectedBlock.contentObject.formattingInstructions ?? [],
    );
    setPositionInstructionsToPaste(positionInstructions);
    startPaste(
      0,
      selectedBlock?.objectId,
      { surveyId: selectedBlock.survey?.objectId, id: selectedBlock.id },
      {
        maxRows: selectedBlock.contentObject.maxRows ?? DefaultMaxRows,
        maxColumns: selectedBlock.contentObject.maxColumns ?? DefaultMaxColumns,
      },
      ({ newPositionInstructions, newTableScale } = {}) => {
        // Apply server-saved changes to local state before getBlocks() resolves so that:
        // 1. DynamicBlocksTable renders new blocks at the right positions (reads selectedBlock.contentObject).
        // 2. usePositioningTable doesn't fire the "unordered blocks" toast (reads blockFormValuesStore.blockValues).
        if (newPositionInstructions || newTableScale) {
          setSelectedBlock((prev) =>
            prev
              ? {
                  ...prev,
                  contentObject: {
                    ...prev.contentObject,
                    ...(newPositionInstructions && { positionInstructions: newPositionInstructions }),
                    ...(newTableScale && { maxRows: newTableScale.maxRows, maxColumns: newTableScale.maxColumns }),
                  },
                }
              : prev,
          );
          setBlockValues((prev) => ({
            ...prev,
            ...(newPositionInstructions && { positionInstructions: newPositionInstructions }),
            ...(newTableScale && { maxRows: newTableScale.maxRows, maxColumns: newTableScale.maxColumns }),
          }));
          setLastBlockValues((prev) => ({
            ...prev,
            ...(newPositionInstructions && { positionInstructions: newPositionInstructions }),
            ...(newTableScale && { maxRows: newTableScale.maxRows, maxColumns: newTableScale.maxColumns }),
          }));
        }
        getBlocks();
        refetchCurrentBlock();
      },
    );
  }, [
    selectedBlock,
    setFormattingInstructionsToPaste,
    startPaste,
    setPositionInstructionsToPaste,
    positionInstructions,
    getBlocks,
    refetchCurrentBlock,
    setBlockValues,
    setLastBlockValues,
  ]);

  useEffect(() => {
    if (
      !surveyBlock ||
      !("contentObject" in surveyBlock!) ||
      ![
        BlockSubTypes.Table as SurveyBlocks.BlockSubType,
        BlockSubTypes.Chart as SurveyBlocks.BlockSubType,
      ].includes((surveyBlock as SurveyBlocks.Block).contentObject.type!)
    ) {
      setTableBlocks(null);
      setSelectedBlock(null);
      setSubBlockToEdit(null);

      return;
    }

    if (surveyBlock.id === selectedBlock?.id) return;

    setSelectedBlock(surveyBlock as SurveyBlocks.Block);
    getBlocks();
  }, [
    selectedBlock,
    getBlocks,
    setSubBlockToEdit,
    setTableBlocks,
    surveyBlock,
  ]);

  if (!isTableEditOpen) return;

  return (
    <div className="layout-table-edit">
      <div className="layout-table-section">
        <CopyPasteBlockActions
          copiedBlocksCount={copiedBlocks?.length ?? 0}
          onPaste={handleOnPaste}
          onClear={handleClearCopiedBlocks}
          onCopy={handleOnCopy}
        />

        <header className="_d-flex-ali-center-sm-gap">
          <BlockIcon blockType={blockToEdit.type} />
          <Typography variant="h4">
            {selectedBlock?.contentObject.title} {isLoading ? "loading" : ""}
          </Typography>
        </header>
        <DynamicBlocksTable
          maxRows={
            selectedBlock?.contentObject.maxRows ??
            BlockDefaults[BlockFieldTypes.MaxRows]
          }
          maxCols={
            selectedBlock?.contentObject.maxColumns ??
            BlockDefaults[BlockFieldTypes.MaxColumns]
          }
          blocks={tableBlocks || []}
          onDrop={handleDrop}
          onDragStart={setDraggedBlockPosition}
          onBlockClick={handleBlockClick}
          onRowSelectionChange={handleRowSelectionChange}
          selectedBlockIds={selectedTableBlocks.map((b) => String(b.id))}
          draggedBlockPosition={draggedBlockPosition}
          setDraggedBlockPosition={setDraggedBlockPosition}
          selectionArea={selectionArea}
          isLoading={isLoadingBlocks}
          isSavingBlockFormValues={isSavingBlockFormValues}
          positionInstructions={positionInstructions}
          handleRepositionBlocks={handleRepositionBlocks}
        />
      </div>
      <div className="layout-table-form-section">
        <ManageDataSet
          mainSurveyStore={props.mainSurveyStore}
          selectedBlock={selectedBlock}
          setSelectedBlock={setSelectedBlock}
          setSelectionArea={setSelectionArea}
          blockFormValuesStore={blockFormValuesStore}
          positionInstructionsStore={positionInstructionsStore}
          onBulkDeleted={handleBulkDeleted}
          onBulkSaved={refreshAfterBulkEdit}
        />
      </div>

      <PasteBlocksDialog
        copyAndPasteBlocksStore={copyAndPasteBlocksStore}
        enableExistingBlocks
      />
    </div>
  );
};

interface TableEditProps {
  mainSurveyStore: MainSurveyStore;
  copyAndPasteBlocksStore: CopyAndPasteBlocksStore;
}
